import { Suspense, lazy, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import './assets/styles/App.css';
import Administrator from './components/Administrator/Administrator';
import CancelTranslation from './components/CancelTranslation/CancelTranslation';
import Company from './components/Company/Company';
import CompanyPage from './components/Company/CompanyPage/CompanyPage';
import Customer from './components/Customer/Customer';
import History from './components/History/History';
import MainLayout from './components/MainLayout/MainLayout';
import Plan from './components/Plan/Plan';
import ProtectedRoute from './components/PrivateRoute';
import PushMessage from './components/Push/Push';
import Reports from './components/Response/Response';
import Translators from './components/Translators/Translators';
import AuthPage from './pages/AuthPage/AuthPage';
import { refreshJwtToken } from './store/actions/authActions';
import { logout } from './store/reducers/authSlice';
import { AppDispatch, RootState } from './store/store';

/* §FB64 (R1/A2): карта — окремий чанк. maplibre-gl і гео-дані не вантажаться,
   поки користувач не відкрив вкладку «Карта WordCraft». */
const WcMapPage = lazy(() => import('./components/WcMap/WcMapPage'));

const MapFallback: React.FC = () => (
  <div
    style={{
      height: '100%',
      minHeight: 320,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#0b1720',
      color: '#2dd4bf',
      fontSize: 15,
      letterSpacing: '0.04em',
    }}
  >
    Завантаження карти…
  </div>
);

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);
  const jwtToken = useSelector((s: RootState) => s.auth.jwtToken);

  // #28: JWT живе лише в пам'яті — після перезавантаження сторінки відновлюємо сесію
  // через httpOnly refresh-cookie. Поки відновлюємо — показуємо лоадер (щоб PrivateRoute
  // не викинув на логін передчасно).
  const [booting, setBooting] = useState<boolean>(isAuthenticated && !jwtToken);

  useEffect(() => {
    if (isAuthenticated && !jwtToken) {
      dispatch(refreshJwtToken())
        .unwrap()
        .catch(() => dispatch(logout()))
        .finally(() => setBooting(false));
    } else {
      setBooting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (booting) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#0a8081',
          fontSize: 16,
        }}
      >
        Завантаження…
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<AuthPage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="company" element={<Company />} />
        {/* §FB37: сторінка компанії з вкладками (deep-link на вкладку в URL). */}
        <Route path="company/:companyId" element={<CompanyPage />} />
        <Route path="company/:companyId/:tab" element={<CompanyPage />} />
        <Route path="customer" element={<Customer />} />
        <Route path="translators" element={<Translators />} />
        <Route path="administrator" element={<Administrator />} />
        <Route path="history" element={<History />} />
        <Route path="cancel_translation" element={<CancelTranslation />} />
        <Route path="reports" element={<Reports />} />
        <Route path="plan" element={<Plan />} />
        {/* §FB47: карта України з локаціями всіх компаній (path = ключ iconsConfig). */}
        <Route
          path="map"
          element={
            <Suspense fallback={<MapFallback />}>
              <WcMapPage />
            </Suspense>
          }
        />
        <Route path="push_message" element={<PushMessage />} />
      </Route>
    </Routes>
  );
};

export default App;
